import { PageUrl } from '../pages/utils/page-url';
import { useCustomPreferenceOrDefault } from '../utils/api';
import { CustomPreference } from '../utils/api/CustomPreference';
import { isElectronicGiftCard, isPhysicalGiftCard } from '../utils/product-utils';
import { useABTestVariable } from './useABTestVariable';
import { useSearchUrl } from './useSearchUrl';
import type { BloomreachProductType } from '../bff-api/bloomreach-recommendation/model/bloomreach';
import type { Product } from '../bff-api/products/model';

interface UseBuildURL {
    buildProductURL: (id: string, type: BloomreachProductType, skuUrl?: string) => string;
    buildSearchCategoryURL: (id: string) => string;
    buildSearchProductURL: (query: string) => string;
    buildCartURL: () => string;
    buildPdpURL: (product: Product, productId?: string) => string;
}

const useBuildURL = (): UseBuildURL => {
    const pwaSearchMode = useABTestVariable<string>(CustomPreference.PWA_SEARCH_MODE, 'SFCC');
    const searchUrl: string = useSearchUrl();
    const pwaPdpEnabled = useCustomPreferenceOrDefault(CustomPreference.PWA_ENABLE_PDP, true);
    const pwaCartEnabled = useCustomPreferenceOrDefault(CustomPreference.PWA_ENABLE_CART, true);

    const buildProductURL = (id: string, type: BloomreachProductType, skuUrl?: string): string => {
        const returnPwaUrl =
            type === 'hardgood' &&
            (pwaPdpEnabled || /^\/product\/.*/.test(window.location.pathname));
        if (skuUrl && returnPwaUrl) {
            return new URL(skuUrl, window.location.href).pathname;
        }
        return returnPwaUrl ? `/product/product/${id}` : `/product/${id}.html`;
    };

    const buildSearchCategoryURL = (id: string): string => `/search?cgid=${id}`;

    const buildSearchProductURL = (query: string): string => {
        const params = new URLSearchParams({
            q: query,
            ...(pwaSearchMode === 'SFCC' ? { prefn1: 'productType', prefv1: 'HardGood' } : {}),
        });
        return `${searchUrl}?${params.toString().replace(/\+/g, '%20')}`;
    };

    const buildCartURL = (): string =>
        pwaCartEnabled ? (PageUrl.PWA_CART as string) : (PageUrl.SG_CART as string);

    const buildPdpURL = (product: Product, productId?: string): string => {
        const params = new URLSearchParams(window.location.search).toString();

        const { id, c_pageURL = '' } = product;
        const sku = productId || id;

        if (isElectronicGiftCard(product)) {
            return `${PageUrl.ELECTRONIC_GIFT_CARD_PAGE.replace(':sku', sku)}?${params}`;
        }

        if (isPhysicalGiftCard(product)) {
            return `${PageUrl.PHYSICAL_GIFT_CARD_PAGE.replace(':sku', sku)}?${params}`;
        }

        return `${PageUrl.PDP.replace(':pageURL', c_pageURL).replace(':sku', sku)}?${params}`;
    };

    return {
        buildProductURL,
        buildSearchCategoryURL,
        buildSearchProductURL,
        buildCartURL,
        buildPdpURL,
    };
};

export { useBuildURL };
